// Minimal toast notifications, stacked bottom-centre.
// createToast returns a handle that can be updated in place (saving → saved)
// or dismissed; toasts auto-dismiss unless given duration: null.

export type ToastKind = 'saving' | 'success' | 'error';

export interface ToastOptions {
  // ms before auto-dismiss; null = sticky. Defaults: success 2500, error/saving sticky.
  duration?: number | null;
  actionLabel?: string;
  onAction?: () => void;
}

export interface ToastHandle {
  set: (kind: ToastKind, message: string, opts?: ToastOptions) => void;
  dismiss: () => void;
  isDismissed: () => boolean;
}

const ICONS: Record<ToastKind, string> = {
  saving: '<span class="toast-spinner"></span>',
  success: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
  error: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
};

function container(): HTMLElement {
  let el = document.getElementById('app-toasts');
  if (!el) {
    el = document.createElement('div');
    el.id = 'app-toasts';
    el.className = 'toast-stack';
    document.body.appendChild(el);
  }
  return el;
}

export function createToast(kind: ToastKind, message: string, opts: ToastOptions = {}): ToastHandle {
  const el = document.createElement('div');
  container().appendChild(el);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let dismissed = false;

  const handle: ToastHandle = {
    set(k: ToastKind, msg: string, o: ToastOptions = {}) {
      if (dismissed) return;
      if (timer) { clearTimeout(timer); timer = null; }
      el.className = `toast toast-${k}`;
      el.innerHTML = ICONS[k];

      const msgEl = document.createElement('span');
      msgEl.className = 'toast-msg';
      msgEl.textContent = msg;
      el.appendChild(msgEl);

      if (o.actionLabel && o.onAction) {
        const btn = document.createElement('button');
        btn.className = 'toast-action';
        btn.textContent = o.actionLabel;
        btn.onclick = () => { const fn = o.onAction!; handle.dismiss(); fn(); };
        el.appendChild(btn);
      }
      if (k === 'error') {
        const close = document.createElement('button');
        close.className = 'toast-close';
        close.setAttribute('aria-label', 'Dismiss');
        close.textContent = '×';
        close.onclick = () => handle.dismiss();
        el.appendChild(close);
      }

      const duration = o.duration !== undefined ? o.duration : (k === 'success' ? 2500 : null);
      if (duration != null) timer = setTimeout(() => handle.dismiss(), duration);
    },
    dismiss() {
      if (dismissed) return;
      dismissed = true;
      if (timer) clearTimeout(timer);
      el.classList.add('toast-out');
      setTimeout(() => el.remove(), 200);
    },
    isDismissed: () => dismissed,
  };

  handle.set(kind, message, opts);
  return handle;
}
